// Filter model for the contact directory: defaults, matching, facet counts and
// session persistence (so filters survive opening a contact and coming back).

import { getFollowUpState, getQuality, getReadiness } from '../../Components/ContactActivity';

export const NO_STATUS = '__none__';

export const FOLLOW_UP_FILTERS = [
  { value: 'due', label: 'Due now (overdue or today)' },
  { value: 'week', label: 'Due in the next 7 days' },
  { value: 'scheduled', label: 'Any follow-up scheduled' },
  { value: 'none', label: 'No follow-up scheduled' },
];

const matchesFollowUp = (contact, filter) => {
  const state = getFollowUpState(contact.nextFollowUpAt);
  if (filter === 'none') return !state;
  if (!state) return false;
  if (filter === 'due') return state.diffDays <= 0;
  if (filter === 'week') return state.diffDays <= 7;
  return true; // scheduled
};

export const DEFAULT_FILTERS = {
  search: '',
  type: 'all', // 'all' | 'person' | 'company'
  statuses: [], // contactStatus values, NO_STATUS for "not set"
  cities: [],
  tags: [],
  sources: [],
  industries: [],
  hasEmail: false,
  hasPhone: false,
  hasWhatsapp: false,
  hasWebsite: false,
  readiness: [], // 'ready' | 'verify' | 'blocked'
  temperatures: [], // 'hot' | 'warm' | 'cold'
  followUp: '', // see FOLLOW_UP_FILTERS
};

const STORAGE_KEY = 'contactDirectory.filters';

export const loadSavedFilters = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    return saved ? { ...DEFAULT_FILTERS, ...saved } : DEFAULT_FILTERS;
  } catch (error) {
    return DEFAULT_FILTERS;
  }
};

export const saveFilters = (filters) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    // Storage unavailable (private mode etc.) – filters just won't persist
  }
};

const isCompany = (c) => c.contactType === 'company';
export const contactName = (c) => (isCompany(c) ? c.companyName : c.fullName) || '';
export const contactEmail = (c) => (isCompany(c) ? c.companyEmail : c.email) || '';
export const contactPhone = (c) => (isCompany(c) ? c.companyPhone : c.phoneNumber) || '';
export const contactWhatsapp = (c) => (isCompany(c) ? c.companyWhatsapp : c.whatsappNumber) || '';
export const contactTags = (c) =>
  (Array.isArray(c.tags) ? c.tags : String(c.tags || '').split(',')).map((t) => t.trim()).filter(Boolean);
export const contactIndustry = (c) => c.industry || c.personIndustry || '';
export const contactCity = (c) => (c.city || '').trim();

const searchableText = (c) =>
  [
    contactName(c),
    contactEmail(c),
    contactPhone(c),
    contactWhatsapp(c),
    c.contactId,
    c.city,
    c.state,
    c.district,
    c.country,
    c.jobTitle,
    c.employer,
    contactIndustry(c),
    c.contactPersonName,
    c.gstNumber,
    c.website,
    getQuality(c).interests,
    ...contactTags(c),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// `skip` leaves one filter out – used to compute counts for that filter's own options
export const matchesFilters = (contact, filters, skip) => {
  if (skip !== 'type' && filters.type !== 'all' && contact.contactType !== filters.type) return false;
  if (skip !== 'statuses' && filters.statuses.length) {
    const status = contact.contactStatus || NO_STATUS;
    if (!filters.statuses.includes(status)) return false;
  }
  if (skip !== 'cities' && filters.cities.length && !filters.cities.includes(contactCity(contact))) return false;
  if (skip !== 'tags' && filters.tags.length && !contactTags(contact).some((t) => filters.tags.includes(t))) return false;
  if (skip !== 'sources' && filters.sources.length && !filters.sources.includes(contact.leadSource)) return false;
  if (skip !== 'industries' && filters.industries.length && !filters.industries.includes(contactIndustry(contact))) return false;
  if (skip !== 'readiness' && filters.readiness.length && !filters.readiness.includes(getReadiness(contact).value)) return false;
  if (skip !== 'temperatures' && filters.temperatures.length && !filters.temperatures.includes(getQuality(contact).leadTemperature)) return false;
  if (skip !== 'followUp' && filters.followUp && !matchesFollowUp(contact, filters.followUp)) return false;
  if (filters.hasEmail && !contactEmail(contact)) return false;
  if (filters.hasPhone && !contactPhone(contact)) return false;
  if (filters.hasWhatsapp && !contactWhatsapp(contact)) return false;
  if (filters.hasWebsite && !String(contact.website || '').trim()) return false;

  const terms = filters.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length) {
    // Every word must appear somewhere, so "ravi pune" finds Ravi in Pune
    const text = searchableText(contact);
    if (!terms.every((term) => text.includes(term))) return false;
  }
  return true;
};

// Option list with counts for a multi-value field, given the other active filters
export const facetOptions = (contacts, filters, key, getValues) => {
  const counts = new Map();
  contacts
    .filter((c) => matchesFilters(c, filters, key))
    .forEach((c) => {
      [].concat(getValues(c)).filter(Boolean).forEach((value) => counts.set(value, (counts.get(value) || 0) + 1));
    });
  // Keep selected values visible even when their count drops to 0
  filters[key].forEach((value) => {
    if (!counts.has(value)) counts.set(value, 0);
  });
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || String(a[0]).localeCompare(String(b[0])))
    .map(([value, count]) => ({ value, count }));
};

export const countActiveFilters = (filters) =>
  (filters.search.trim() ? 1 : 0) +
  (filters.type !== 'all' ? 1 : 0) +
  filters.statuses.length +
  filters.cities.length +
  filters.tags.length +
  filters.sources.length +
  filters.industries.length +
  (filters.hasEmail ? 1 : 0) +
  (filters.hasPhone ? 1 : 0) +
  (filters.hasWhatsapp ? 1 : 0) +
  (filters.hasWebsite ? 1 : 0) +
  filters.readiness.length +
  filters.temperatures.length +
  (filters.followUp ? 1 : 0);

// Filters inside the "More filters" panel (search, type and status are always visible)
export const countPanelFilters = (filters) =>
  filters.cities.length +
  filters.tags.length +
  filters.sources.length +
  filters.industries.length +
  (filters.hasEmail ? 1 : 0) +
  (filters.hasPhone ? 1 : 0) +
  (filters.hasWhatsapp ? 1 : 0) +
  (filters.hasWebsite ? 1 : 0) +
  filters.readiness.length +
  filters.temperatures.length +
  (filters.followUp ? 1 : 0);
